export type ConsentSignal = "granted" | "denied";

/**
 * Four-bucket category model (OneTrust / IAB TCF style). Each category maps
 * to one or more Google Consent Mode v2 signals:
 *
 *   necessary   → security_storage (always granted; never consent-gated)
 *   performance → analytics_storage
 *   functional  → functionality_storage + personalization_storage
 *   targeting   → ad_storage + ad_user_data + ad_personalization
 */
export const CONSENT_CATEGORIES = [
  "necessary",
  "performance",
  "functional",
  "targeting",
] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/**
 * Full Consent Mode v2 surface. Seven signals: four ad/analytics + two
 * functional + one security. `wait_for_update` is a defaults-only knob, not
 * a signal — we pass it alongside the defaults call.
 */
export interface ConsentChoice {
  analytics_storage: ConsentSignal;
  ad_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
  functionality_storage: ConsentSignal;
  personalization_storage: ConsentSignal;
  security_storage: ConsentSignal;
}

/**
 * Default-denied baseline. `security_storage` stays granted — it's for
 * CSRF / fraud-detection cookies which regulators don't require consent
 * for, so starting it as granted avoids breaking login/security flows
 * before the user makes a choice.
 */
export const ALL_DENIED: ConsentChoice = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  security_storage: "granted",
};

export const ALL_GRANTED: ConsentChoice = {
  analytics_storage: "granted",
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  functionality_storage: "granted",
  personalization_storage: "granted",
  security_storage: "granted",
};

export function choiceFromCategories(
  categories: readonly ConsentCategory[],
): ConsentChoice {
  const set = new Set(categories);
  const performance: ConsentSignal = set.has("performance") ? "granted" : "denied";
  const functional: ConsentSignal = set.has("functional") ? "granted" : "denied";
  const targeting: ConsentSignal = set.has("targeting") ? "granted" : "denied";
  return {
    security_storage: "granted",
    analytics_storage: performance,
    functionality_storage: functional,
    personalization_storage: functional,
    ad_storage: targeting,
    ad_user_data: targeting,
    ad_personalization: targeting,
  };
}

export function categoriesFromChoice(choice: ConsentChoice): ConsentCategory[] {
  const cats: ConsentCategory[] = ["necessary"];
  if (choice.analytics_storage === "granted") cats.push("performance");
  if (choice.functionality_storage === "granted") cats.push("functional");
  if (choice.ad_storage === "granted") cats.push("targeting");
  return cats;
}
